/**
 * Финансовый блок — стартовый экран для тех, кому выданы финансовые отчёты.
 *
 * Пять разделов: обзор, P&L, Cash Flow, дебиторка/кредиторка, маржинальность.
 * Период и город живут в адресе (`?p=month:2026-09&b=branch:EKB`), поэтому
 * ссылкой на срез можно поделиться, а смена раздела их сохраняет.
 */

import { Icon } from '../components/Icon'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import type { CurrentUser, DimensionValue } from '../types'
import { FinanceContext, type DrillTarget, type FinanceContextValue, type SectionCode } from './context'
import { financeAccess, useCatalog, type FinanceReport } from './data'
import { buildContext, ClosedSection, FrameNotices, useBlockFrame } from './frame'
import { Drawer, type DrawerOrigin } from './Drawer'
import { DrillContent, drillSubtitle, drillTitle } from './Drill'
import { durationToken, withViewTransition } from './motion'
import { Freshness, KpiSkeleton, Segmented } from './parts'
import { useFreshness } from './freshness'
import { RELATION_EXTERNAL, useFinanceSettings } from './settings'
import { formatSelection, isFuture, labelOf, monthLabel, normalize, parseSelection, shift, type Grain } from './period'
import type { IconName } from '../components/Icon'
import { Overview } from './sections/Overview'
import { Pnl } from './sections/Pnl'
import { Cash } from './sections/Cash'
import { Debts } from './sections/Debts'
import { Margin } from './sections/Margin'
import './finance.css'

export interface FinanceSection {
  code: SectionCode
  title: string
  short: string
  icon: IconName
  subtitle: string
}

export const FINANCE_SECTIONS: FinanceSection[] = [
  { code: 'overview', title: 'Финансовый обзор', short: 'Обзор', icon: 'overview', subtitle: 'Главные цифры периода и что требует внимания' },
  { code: 'pnl', title: 'P&L', short: 'P&L', icon: 'pnl', subtitle: 'Выручка, валовая прибыль, расходы и черновая EBITDA' },
  { code: 'cash', title: 'Cash Flow', short: 'Cash Flow', icon: 'cash', subtitle: 'Поступления и списания по статьям ДДС' },
  { code: 'debts', title: 'Дебиторка и кредиторка', short: 'Дебиторка / кредиторка', icon: 'debts', subtitle: 'Долги на последнюю дату и сроки просрочки' },
  { code: 'margin', title: 'Маржинальность', short: 'Маржинальность', icon: 'margin', subtitle: 'Рентабельность по направлениям, группам и клиентам' },
]

export function sectionPath(code: SectionCode): string {
  return code === 'overview' ? '/' : `/finance/${code}`
}

export function visibleSections(user: CurrentUser): FinanceSection[] {
  const access = financeAccess(user)
  return FINANCE_SECTIONS.filter((section) => access[section.code])
}

/** Ссылка на раздел с сохранением периода и города, сменой через View Transitions. */
export function useSectionNavigator() {
  const navigate = useNavigate()
  const location = useLocation()
  return useCallback(
    (code: SectionCode, extra?: Record<string, string>) => {
      const current = new URLSearchParams(location.search)
      const next = new URLSearchParams()
      for (const key of ['p', 'b']) {
        const value = current.get(key)
        if (value) next.set(key, value)
      }
      for (const [key, value] of Object.entries(extra ?? {})) next.set(key, value)
      const query = next.toString()
      const target = `${sectionPath(code)}${query ? `?${query}` : ''}`
      if (`${location.pathname}${location.search}` === target) return
      withViewTransition(() => navigate(target))
      window.scrollTo({ top: 0 })
    },
    [navigate, location.pathname, location.search],
  )
}

export function useCurrentSection(): SectionCode {
  const { section } = useParams()
  return (FINANCE_SECTIONS.find((item) => item.code === section)?.code ?? 'overview') as SectionCode
}

export function FinanceBlock({ user }: { user: CurrentUser }) {
  const section = useCurrentSection()
  const { section: requested } = useParams()
  const catalog = useCatalog(user)
  const go = useSectionNavigator()
  const sections = visibleSections(user)
  const frame = useBlockFrame(user, catalog, ['margin_by_department', 'sales_by_group', 'abc_customers'])
  const { selection, branch, branches, restricted, scopeLabel, patch } = frame
  const { settings, ready: settingsReady } = useFinanceSettings()
  // Долги между своими не входят в итог, пока клиент не решил иначе.
  const debtsExternalOnly = settings.debts_intercompany === 'exclude' && catalog.hasDimension('receivables', 'relation')

  const [drillState, setDrillState] = useState<{ target: DrillTarget; origin: DrawerOrigin } | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const value = useMemo<FinanceContextValue>(
    () =>
      buildContext(frame, user, catalog, {
        receivableFilter: {
          ...(catalog.hasDimension('receivables', 'kind') ? { kind: ['Дебиторская'] } : {}),
          ...(debtsExternalOnly ? { relation: [RELATION_EXTERNAL] } : {}),
        } as Record<string, string[]>,
        debtsExternalOnly,
        go,
        drill: (target, origin) => {
          setDrillState({ target, origin })
          setDrawerOpen(true)
        },
      }),
    // frame пересоздаётся на каждом рендере; значимые части перечислены явно.
    [user, catalog, selection, branch, frame.branchName, scopeLabel, restricted, go, frame.coverage, frame.branches, debtsExternalOnly],
  )

  if (!sections.some((item) => item.code === section)) {
    const first = sections[0]
    // Глубокая ссылка на закрытый раздел — карточка с причиной. Главная «/»
    // без обзора — сразу на первый доступный раздел: это не ссылка, а вход.
    if (requested && FINANCE_SECTIONS.some((item) => item.code === requested)) {
      const closed = FINANCE_SECTIONS.find((item) => item.code === requested)!
      // Причину «не делится по городам» видно только по описаниям отчётов.
      if (restricted && !catalog.ready && !user.reports.some((report) => report.unavailable_reason)) return <SectionSkeleton />
      const scopeReason =
        SECTION_REPORTS[closed.code]
          .map((report) => value.scopeBlocked(report.code as FinanceReport))
          .find((text): text is string => Boolean(text)) ?? null
      return (
        <ClosedSection
          title={closed.title}
          sectionCode={`finance.${closed.code}`}
          reports={SECTION_REPORTS[closed.code]}
          user={user}
          scopeReason={scopeReason}
          fallback={first ? { path: sectionPath(first.code), label: `Открыть «${first.title}»` } : { path: '/summary', label: 'Открыть сводку' }}
        />
      )
    }
    return <Navigate to={first ? sectionPath(first.code) : '/summary'} replace />
  }

  return (
    <FinanceContext.Provider value={value}>
      <div className="fin">
        <Toolbar
          selection={selection}
          onSelection={(next) => patch({ p: formatSelection(next) })}
          branch={branch}
          branches={branches}
          restricted={restricted}
          onBranch={(next) => patch({ b: next })}
          periodApplies={section !== 'debts'}
          lastSales={frame.lastSales}
        />
        <FrameNotices notices={frame.notices} onDismiss={frame.dismissBranchNotice} />

        <nav className="fin-tabs" aria-label="Разделы финансового блока">
          {sections.map((item) => (
            <a
              key={item.code}
              href={sectionPath(item.code)}
              className={`fin-tab${item.code === section ? ' fin-tab--active' : ''}`}
              aria-current={item.code === section ? 'page' : undefined}
              onClick={(event) => {
                event.preventDefault()
                go(item.code)
              }}
            >
              {item.code === section && <span className="fin-tab__pill" aria-hidden="true" />}
              <span className="fin-tab__text">{item.short}</span>
            </a>
          ))}
        </nav>

        <div className="fin-section" key={section}>
          {!catalog.ready || !settingsReady ? <SectionSkeleton /> : <SectionBody section={section} />}
        </div>
      </div>

      <Drawer
        open={drawerOpen}
        origin={drillState?.origin ?? null}
        onClose={() => setDrawerOpen(false)}
        title={drillState ? drillTitle(drillState.target) : ''}
        subtitle={
          drillState
            ? drillState.target.kind === 'debtor'
              ? `${drillSubtitle(drillState.target)} · на последнюю дату`
              : `${drillSubtitle(drillState.target)} · ${labelOf(selection)} · ${scopeLabel}`
            : undefined
        }
      >
        {drillState && <DrillContent target={drillState.target} />}
      </Drawer>
    </FinanceContext.Provider>
  )
}

/** На каких отчётах построен раздел — для объяснения, почему он закрыт. */
const REPORT_TITLES: Record<string, string> = {
  margin_by_department: 'Маржинальность по филиалам',
  sales_by_group: 'Выручка по направлениям',
  abc_customers: 'ABC-анализ клиентов',
  cashflow: 'Движение денежных средств',
  receivables: 'Дебиторская и кредиторская задолженность',
  pnl_management: 'Управленческий P&L (черновой)',
  payroll_services: 'ФОТ по поступлениям услуг',
  stock_current: 'Остатки и неликвиды',
  stock_turnover: 'Оборачиваемость и рентабельность запасов',
}

export const reportRefs = (...codes: string[]) => codes.map((code) => ({ code, title: REPORT_TITLES[code] ?? code }))

const SECTION_REPORTS: Record<SectionCode, Array<{ code: string; title: string }>> = {
  overview: reportRefs('margin_by_department', 'sales_by_group', 'abc_customers', 'cashflow', 'receivables'),
  pnl: reportRefs('margin_by_department', 'pnl_management'),
  cash: reportRefs('cashflow'),
  debts: reportRefs('receivables'),
  margin: reportRefs('margin_by_department', 'sales_by_group', 'abc_customers'),
}

function SectionBody({ section }: { section: SectionCode }) {
  if (section === 'pnl') return <Pnl />
  if (section === 'cash') return <Cash />
  if (section === 'debts') return <Debts />
  if (section === 'margin') return <Margin />
  return <Overview />
}

function SectionSkeleton() {
  return (
    <div className="fin-stack" aria-busy="true" aria-label="Загрузка">
      <div className="kpi-grid">
        {Array.from({ length: 4 }, (_, index) => (
          <KpiSkeleton key={index} index={index} />
        ))}
      </div>
      <div className="skeleton" style={{ height: 320, borderRadius: 'var(--radius-lg)' }} />
    </div>
  )
}

// --- Панель периода и города ------------------------------------------------

const GRAINS: Array<{ value: Grain; label: string }> = [
  { value: 'month', label: 'Месяц' },
  { value: 'quarter', label: 'Квартал' },
  { value: 'year', label: 'Год' },
]

export function Toolbar({
  selection,
  onSelection,
  branch,
  branches,
  restricted,
  onBranch,
  periodApplies,
  lastSales = null,
}: {
  selection: ReturnType<typeof parseSelection>
  onSelection: (next: ReturnType<typeof parseSelection>) => void
  branch: string | null
  branches: DimensionValue[] | null
  restricted: boolean
  onBranch: (next: string | null) => void
  periodApplies: boolean
  /** Выгрузка отстаёт: последний месяц с продажами. */
  lastSales?: string | null
}) {
  const asOf = useFreshness()
  const direction = useRef<'back' | 'forward'>('forward')
  const nextDisabled = isFuture(shift(selection, 1))

  const step = (steps: number) => {
    direction.current = steps < 0 ? 'back' : 'forward'
    onSelection(shift(selection, steps))
  }

  return (
    <div className="fin-toolbar">
      <div className={`period${periodApplies ? '' : ' period--inactive'}`} title={periodApplies ? undefined : 'Долги — на последнюю дату, период к ним не применяется'}>
        <button type="button" className="period__step" onClick={() => step(-1)} aria-label="Предыдущий период">
          <Icon name="chevron-left" />
        </button>
        <span className="period__label" aria-live="polite">
          <SlideText value={labelOf(selection)} direction={direction.current} />
        </span>
        <button type="button" className="period__step" onClick={() => step(1)} disabled={nextDisabled} aria-label="Следующий период">
          <Icon name="chevron-right" />
        </button>
      </div>

      <Segmented
        label="Длина периода"
        value={selection.grain}
        onChange={(grain) => onSelection(normalize({ grain, start: selection.start }))}
        options={GRAINS}
      />

      {restricted && branches?.length === 1 ? (
        // Один доступный город: выбирать не из чего, «Все доступные города»
        // отдельным пунктом означали бы то же самое.
        <span className="city city--single" title="Вам открыт один город">
          <Icon name="pin" className="city__icon" />
          <span className="city__select">{branches[0]!.name}</span>
        </span>
      ) : (
      <label className="city">
        <Icon name="pin" className="city__icon" />
        <select
          className="city__select"
          value={branch ?? ''}
          onChange={(event) => onBranch(event.target.value || null)}
          aria-label="Город"
          disabled={!branches || branches.length === 0}
        >
          <option value="">{restricted ? 'Все доступные города' : 'Все города'}</option>
          {(branches ?? []).map((item) => (
            <option key={item.guid} value={item.guid}>
              {item.name}
            </option>
          ))}
        </select>
        <Icon name="chevron-down" size={14} className="city__chevron" />
      </label>
      )}

      <span className="spacer" />
      <Freshness asOf={asOf.asOf} failed={asOf.failed} lastSales={lastSales ? monthLabel(lastSales) : null} />
    </div>
  )
}

/** Подпись периода уезжает в сторону шага: назад — вправо, вперёд — влево. */
function SlideText({ value, direction }: { value: string; direction: 'back' | 'forward' }): ReactNode {
  const [items, setItems] = useState<Array<{ value: string; id: number; state: 'in' | 'out' }>>([{ value, id: 0, state: 'in' }])
  const counter = useRef(0)

  useEffect(() => {
    setItems((current) => {
      if (current[current.length - 1]?.value === value) return current
      counter.current += 1
      return [...current.filter((item) => item.state === 'in').map((item) => ({ ...item, state: 'out' as const })), { value, id: counter.current, state: 'in' }]
    })
  }, [value])

  useEffect(() => {
    if (!items.some((item) => item.state === 'out')) return
    const id = window.setTimeout(
      () => setItems((current) => current.filter((item) => item.state === 'in')),
      durationToken('--dur-section', 240) + 40,
    )
    return () => window.clearTimeout(id)
  }, [items])

  return (
    <span className={`slide slide--${direction}`}>
      {items.map((item) => (
        <span key={item.id} className={`slide__item slide__item--${item.id === 0 && item.state === 'in' ? 'first' : item.state}`} aria-hidden={item.state === 'out'}>
          {item.value}
        </span>
      ))}
    </span>
  )
}
